import { useState, useEffect, useCallback } from "react";
import {
  Save,
  Loader2,
  Users,
  TableProperties,
  ClockArrowUp,
  BadgeCheck,
  ShieldQuestion,
  Gauge,
  CalendarDays,
  Clock,
  Info,
} from "lucide-react";
import { useLocation, useBookingMode } from "../contexts/LocationContext";
import { getAuthToken } from "../lib/api-client";
import { useGlobalToast } from "../contexts/ToastContext";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:4000/api";

interface BookingConfig {
  slotInterval: number;
  defaultDuration: number;
  maxPartySize: number;
  minAdvanceHours: number;
  maxAdvanceDays: number;
  tableReleaseFrom: string;
  singleTurnPerShift: boolean;
  autoConfirmReservations: boolean;
  useTableAssignment: boolean;
  maxCapacityPerService: number | null;
  largeGroupThreshold: number;
}

const DEFAULT_CONFIG: BookingConfig = {
  slotInterval: 30,
  defaultDuration: 90,
  maxPartySize: 12,
  minAdvanceHours: 2,
  maxAdvanceDays: 30,
  tableReleaseFrom: "reservation_time",
  singleTurnPerShift: false,
  autoConfirmReservations: true,
  useTableAssignment: true,
  maxCapacityPerService: null,
  largeGroupThreshold: 15,
};

export default function LocationSettings() {
  const { currentLocationId, locationVersion } = useLocation();
  const bookingMode = useBookingMode();
  const isAppointmentsMode = bookingMode === "appointments";
  const { showToast } = useGlobalToast();
  const [config, setConfig] = useState<BookingConfig>(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [reservationStatuses, setReservationStatuses] = useState<
    { code: string; name: string }[]
  >([]);

  // Raw string state for numeric inputs to avoid zero-prefix issues
  const [rawMaxPartySize, setRawMaxPartySize] = useState("12");
  const [rawMaxAdvanceDays, setRawMaxAdvanceDays] = useState("30");
  const [rawMaxCapacity, setRawMaxCapacity] = useState("");
  const [rawLargeGroupThreshold, setRawLargeGroupThreshold] = useState("15");

  const getAuthHeaders = () => {
    const locationId =
      currentLocationId || localStorage.getItem("rm_current_location_id");
    const organizationId = localStorage.getItem("rm_current_organization_id");
    const token = getAuthToken();
    return {
      "Content-Type": "application/json",
      ...(locationId ? { "X-Location-Id": locationId } : {}),
      ...(organizationId ? { "X-Organization-Id": organizationId } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  };

  const authFetch = (url: string, options: RequestInit = {}) =>
    fetch(url, {
      ...options,
      headers: {
        ...getAuthHeaders(),
        ...(options.headers as Record<string, string>),
      },
      credentials: "include",
    });

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API_URL}/schedule/config`);
      if (res.ok) {
        const data = await res.json();
        const c = data.config;
        const merged: BookingConfig = {
          slotInterval: c.slotInterval ?? 30,
          defaultDuration: c.defaultDuration ?? 90,
          maxPartySize: c.maxPartySize ?? 12,
          minAdvanceHours: c.minAdvanceHours ?? 2,
          maxAdvanceDays: c.maxAdvanceDays ?? 30,
          tableReleaseFrom: c.tableReleaseFrom ?? "reservation_time",
          singleTurnPerShift: c.singleTurnPerShift ?? false,
          autoConfirmReservations: c.autoConfirmReservations ?? true,
          useTableAssignment: c.useTableAssignment ?? true,
          maxCapacityPerService: c.maxCapacityPerService ?? null,
          largeGroupThreshold: c.largeGroupThreshold ?? 15,
        };
        setConfig(merged);
        setRawMaxPartySize(String(merged.maxPartySize));
        setRawMaxAdvanceDays(String(merged.maxAdvanceDays));
        setRawMaxCapacity(
          merged.maxCapacityPerService != null
            ? String(merged.maxCapacityPerService)
            : "",
        );
        setRawLargeGroupThreshold(String(merged.largeGroupThreshold));
      }
    } catch (error) {
      console.error("Error loading config:", error);
    } finally {
      setLoading(false);
    }
  }, [currentLocationId]);

  const loadStatuses = useCallback(async () => {
    try {
      const res = await authFetch(`${API_URL}/reservations/statuses`);
      if (res.ok) {
        const data = await res.json();
        setReservationStatuses(
          data.data
            .filter((s: { isFinal: boolean }) => !s.isFinal)
            .map((s: { code: string; name: string }) => ({
              code: s.code,
              name: s.name,
            })),
        );
      }
    } catch (_) {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    loadConfig();
    loadStatuses();
  }, [loadConfig, loadStatuses, locationVersion]);

  const saveConfig = async () => {
    setSaving(true);
    try {
      const payload: Record<string, unknown> = {
        slotInterval: config.slotInterval,
        defaultDuration: config.defaultDuration,
        maxPartySize: config.maxPartySize,
        minAdvanceHours: config.minAdvanceHours,
        maxAdvanceDays: config.maxAdvanceDays,
        tableReleaseFrom: config.tableReleaseFrom,
        singleTurnPerShift: config.singleTurnPerShift,
        autoConfirmReservations: config.autoConfirmReservations,
        useTableAssignment: config.useTableAssignment,
        maxCapacityPerService: config.maxCapacityPerService,
        largeGroupThreshold: config.largeGroupThreshold,
      };
      const res = await authFetch(`${API_URL}/schedule/config`, {
        method: "PUT",
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        showToast("Configuración guardada correctamente");
      } else {
        const err = await res.json();
        showToast(err.message || "Error al guardar", "error");
      }
    } catch {
      showToast("Error de conexión", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  const toggle = (
    field:
      | "autoConfirmReservations"
      | "useTableAssignment"
      | "singleTurnPerShift",
  ) => setConfig((prev) => ({ ...prev, [field]: !prev[field] }));

  return (
    <div className="space-y-6 pb-10">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900">
            Configuración del Local
          </h1>
          <p className="text-gray-500 mt-1 text-sm">
            {isAppointmentsMode
              ? "Ajusta cómo funciona el sistema de citas para tu negocio"
              : "Ajusta cómo funciona el sistema de reservas para tu restaurante"}
          </p>
        </div>
        <button
          onClick={saveConfig}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors text-sm font-medium"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          {saving ? "Guardando..." : "Guardar cambios"}
        </button>
      </div>

      {/* ── SECTION 1: Confirmación de reservas ── */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 bg-gray-50">
          <div className="flex items-center gap-2">
            <BadgeCheck className="w-5 h-5 text-primary-600" />
            <h2 className="text-base font-semibold text-gray-900">
              Confirmación de reservas
            </h2>
          </div>
          <p className="text-xs text-gray-500 mt-0.5">
            Controla si las reservas se confirman automáticamente o requieren tu
            aprobación
          </p>
        </div>
        <div className="p-5 space-y-5">
          {/* Auto-confirm toggle */}
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <BadgeCheck className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-gray-900 text-sm">
                  Confirmar reservas automáticamente
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {config.autoConfirmReservations
                    ? "Las reservas del widget, VAPI y manual quedan confirmadas al instante."
                    : "Todas las reservas quedan como Pendiente hasta que tú las apruebes manualmente."}
                </p>
              </div>
            </div>
            <button
              onClick={() => toggle("autoConfirmReservations")}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${
                config.autoConfirmReservations
                  ? "bg-primary-600"
                  : "bg-gray-300"
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                  config.autoConfirmReservations
                    ? "translate-x-6"
                    : "translate-x-1"
                }`}
              />
            </button>
          </div>

          {!config.autoConfirmReservations && (
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3">
              <Info className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-700">
                Con la confirmación manual activada, el cliente verá su reserva
                como "Pendiente de confirmación". Recibirá un email de
                confirmación solo cuando tú cambies el estado a Confirmada.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* ── SECTION 2: Asignación de mesas (solo modo MESAS) ── */}
      {!isAppointmentsMode && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 bg-gray-50">
            <div className="flex items-center gap-2">
              <TableProperties className="w-5 h-5 text-primary-600" />
              <h2 className="text-base font-semibold text-gray-900">
                Asignación de mesas
              </h2>
            </div>
            <p className="text-xs text-gray-500 mt-0.5">
              Elige cómo gestionar la disponibilidad: por mesas o por aforo
              total
            </p>
          </div>
          <div className="p-5 space-y-5">
            {/* Use table assignment toggle */}
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <TableProperties className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-gray-900 text-sm">
                    Asignar a mesa específica
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {config.useTableAssignment
                      ? "El sistema asigna automáticamente la mesa más adecuada según capacidad y disponibilidad."
                      : "No se asigna mesa. La disponibilidad se controla únicamente por aforo máximo por servicio."}
                  </p>
                </div>
              </div>
              <button
                onClick={() => toggle("useTableAssignment")}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${
                  config.useTableAssignment ? "bg-primary-600" : "bg-gray-300"
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                    config.useTableAssignment
                      ? "translate-x-6"
                      : "translate-x-1"
                  }`}
                />
              </button>
            </div>

            {/* Single turn per shift */}
            {config.useTableAssignment && (
              <div className="flex items-start justify-between gap-4 pt-4 border-t border-gray-100">
                <div className="flex items-start gap-3">
                  <ClockArrowUp className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-gray-900 text-sm">
                      Un solo turno por servicio
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Cada mesa se ocupa para todo el turno (sin segunda
                      rotación). La duración de la reserva se ignora a efectos
                      de disponibilidad.
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => toggle("singleTurnPerShift")}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${
                    config.singleTurnPerShift ? "bg-primary-600" : "bg-gray-300"
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                      config.singleTurnPerShift
                        ? "translate-x-6"
                        : "translate-x-1"
                    }`}
                  />
                </button>
              </div>
            )}

            {/* Table release from */}
            {config.useTableAssignment && (
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 pt-4 border-t border-gray-100">
                <div className="flex items-start gap-3 flex-1">
                  <ClockArrowUp className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-gray-900 text-sm">
                      Liberar mesa desde
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      A partir de qué momento empieza a contar el tiempo de
                      ocupación
                    </p>
                  </div>
                </div>
                <select
                  value={config.tableReleaseFrom}
                  onChange={(e) =>
                    setConfig((prev) => ({
                      ...prev,
                      tableReleaseFrom: e.target.value,
                    }))
                  }
                  className="w-full sm:w-56 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="reservation_time">Hora de la reserva</option>
                  {reservationStatuses.map((s) => (
                    <option key={s.code} value={s.code}>
                      Cuando llega a «{s.name}»
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Capacity-based info */}
            {!config.useTableAssignment && (
              <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-lg p-3">
                <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-blue-700">
                  En modo por aforo, configura el máximo de comensales por
                  servicio en la sección "Aforo máximo" de más abajo. El
                  widget y la centralita IA respetarán ese límite.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── SECTION 3: Aforo máximo (solo modo MESAS) ── */}
      {!isAppointmentsMode && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 bg-gray-50">
            <div className="flex items-center gap-2">
              <Gauge className="w-5 h-5 text-primary-600" />
              <h2 className="text-base font-semibold text-gray-900">
                Aforo máximo por servicio
              </h2>
            </div>
            <p className="text-xs text-gray-500 mt-0.5">
              Limita cuántos comensales puede haber en cada turno (comida /
              cena)
            </p>
          </div>
          <div className="p-5 space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {/* Max capacity per service */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  <Gauge className="w-4 h-4 inline-block mr-1 text-gray-400" />
                  Aforo máximo por turno
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    placeholder="Sin límite"
                    value={rawMaxCapacity}
                    onChange={(e) => {
                      const v = e.target.value.replace(/[^0-9]/g, "");
                      setRawMaxCapacity(v);
                      setConfig((prev) => ({
                        ...prev,
                        maxCapacityPerService: v === "" ? null : Number(v),
                      }));
                    }}
                    onFocus={(e) => e.target.select()}
                    onBlur={() => {
                      if (
                        rawMaxCapacity !== "" &&
                        Number(rawMaxCapacity) < 1
                      ) {
                        setRawMaxCapacity("");
                        setConfig((prev) => ({
                          ...prev,
                          maxCapacityPerService: null,
                        }));
                      }
                    }}
                    onWheel={(e) => e.currentTarget.blur()}
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-500 whitespace-nowrap">
                    personas
                  </span>
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  Vacío = sin límite de aforo
                </p>
              </div>

              {/* Max party size */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  <Users className="w-4 h-4 inline-block mr-1 text-gray-400" />
                  Máximo de personas por reserva
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={rawMaxPartySize}
                    onChange={(e) => {
                      const v = e.target.value.replace(/[^0-9]/g, "");
                      setRawMaxPartySize(v);
                      if (v !== "")
                        setConfig((prev) => ({
                          ...prev,
                          maxPartySize: Number(v),
                        }));
                    }}
                    onFocus={(e) => e.target.select()}
                    onBlur={() => {
                      const n = Number(rawMaxPartySize);
                      if (!n || n < 1) {
                        setRawMaxPartySize("12");
                        setConfig((prev) => ({ ...prev, maxPartySize: 12 }));
                      }
                    }}
                    onWheel={(e) => e.currentTarget.blur()}
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-500 whitespace-nowrap">
                    personas
                  </span>
                </div>
              </div>
            </div>

            {/* Large group authorization threshold */}
            <div className="pt-4 border-t border-gray-100">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                <ShieldQuestion className="w-4 h-4 inline-block mr-1 text-gray-400" />
                Autorización para grupos grandes
              </label>
              <div className="flex items-center gap-2 max-w-[220px]">
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={rawLargeGroupThreshold}
                  onChange={(e) => {
                    const v = e.target.value.replace(/[^0-9]/g, "");
                    setRawLargeGroupThreshold(v);
                    if (v !== "")
                      setConfig((prev) => ({
                        ...prev,
                        largeGroupThreshold: Math.min(Number(v), 100),
                      }));
                  }}
                  onFocus={(e) => e.target.select()}
                  onBlur={() => {
                    const n = Number(rawLargeGroupThreshold);
                    if (!n || n < 1) {
                      setRawLargeGroupThreshold("15");
                      setConfig((prev) => ({
                        ...prev,
                        largeGroupThreshold: 15,
                      }));
                    } else if (n > 100) {
                      setRawLargeGroupThreshold("100");
                      setConfig((prev) => ({
                        ...prev,
                        largeGroupThreshold: 100,
                      }));
                    }
                  }}
                  onWheel={(e) => e.currentTarget.blur()}
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-500 whitespace-nowrap">
                  personas
                </span>
              </div>
              <p className="text-xs text-gray-400 mt-1">
                Las reservas online de más de{" "}
                <strong>{config.largeGroupThreshold || 15}</strong> personas
                quedarán pendientes hasta que las apruebes.
              </p>
            </div>

            {config.maxCapacityPerService != null && (
              <div className="flex items-start gap-2 bg-green-50 border border-green-200 rounded-lg p-3">
                <ShieldQuestion className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-green-700">
                  El widget y la centralita IA rechazarán automáticamente
                  reservas cuando el turno supere{" "}
                  <strong>{config.maxCapacityPerService} personas</strong>.
                  Límite separado para comida y cena (franja &lt;17:00 /
                  ≥17:00).
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── SECTION 4: Slots y duración (solo modo MESAS: en citas el intervalo
           real se configura en Horarios → Reglas de reserva) ── */}
      {!isAppointmentsMode && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 bg-gray-50">
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-primary-600" />
              <h2 className="text-base font-semibold text-gray-900">
                Slots y duración
              </h2>
            </div>
            <p className="text-xs text-gray-500 mt-0.5">
              Define los intervalos de tiempo disponibles en el widget
            </p>
          </div>
          <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {/* Slot interval */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Intervalo entre horas disponibles
              </label>
              <select
                value={config.slotInterval}
                onChange={(e) =>
                  setConfig((prev) => ({
                    ...prev,
                    slotInterval: Number(e.target.value),
                  }))
                }
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value={15}>15 minutos</option>
                <option value={30}>30 minutos</option>
                <option value={60}>60 minutos</option>
              </select>
            </div>

            {/* Default duration */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Duración por defecto de reserva
              </label>
              <select
                value={config.defaultDuration}
                onChange={(e) =>
                  setConfig((prev) => ({
                    ...prev,
                    defaultDuration: Number(e.target.value),
                  }))
                }
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value={60}>60 minutos</option>
                <option value={90}>90 minutos</option>
                <option value={120}>120 minutos</option>
                <option value={150}>150 minutos</option>
                <option value={180}>180 minutos</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* ── SECTION 5: Antelación ── */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 bg-gray-50">
          <div className="flex items-center gap-2">
            <CalendarDays className="w-5 h-5 text-primary-600" />
            <h2 className="text-base font-semibold text-gray-900">
              Antelación para reservar
            </h2>
          </div>
          <p className="text-xs text-gray-500 mt-0.5">
            Cuánto tiempo antes (mínimo) y con cuántos días de antelación
            (máximo) se puede reservar
          </p>
        </div>
        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-5">
          {/* Min advance time (selector con opciones en min/horas) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Antelación mínima
            </label>
            <select
              value={String(config.minAdvanceHours)}
              onChange={(e) => {
                const v = Number(e.target.value);
                setConfig((prev) => ({ ...prev, minAdvanceHours: v }));
              }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
            >
              <option value="0">Sin antelación (en el momento)</option>
              <option value="0.25">15 minutos antes</option>
              <option value="0.5">30 minutos antes</option>
              <option value="0.75">45 minutos antes</option>
              <option value="1">1 hora antes</option>
              <option value="1.5">1 hora y 30 minutos antes</option>
              <option value="2">2 horas antes</option>
              <option value="3">3 horas antes</option>
              <option value="4">4 horas antes</option>
              <option value="6">6 horas antes</option>
              <option value="12">12 horas antes</option>
              <option value="24">24 horas antes (1 día)</option>
              <option value="48">48 horas antes (2 días)</option>
              <option value="72">72 horas antes (3 días)</option>
            </select>
            <p className="text-xs text-gray-400 mt-1">
              Tiempo mínimo desde que se hace la reserva hasta la hora reservada
            </p>
          </div>

          {/* Max advance days */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Antelación máxima (días)
            </label>
            <div className="flex items-center gap-2">
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={rawMaxAdvanceDays}
                onChange={(e) => {
                  const v = e.target.value.replace(/[^0-9]/g, "");
                  setRawMaxAdvanceDays(v);
                  if (v !== "")
                    setConfig((prev) => ({
                      ...prev,
                      maxAdvanceDays: Number(v),
                    }));
                }}
                onFocus={(e) => e.target.select()}
                onBlur={() => {
                  const n = Number(rawMaxAdvanceDays);
                  if (!n || n < 1) {
                    setRawMaxAdvanceDays("30");
                    setConfig((prev) => ({ ...prev, maxAdvanceDays: 30 }));
                  }
                }}
                onWheel={(e) => e.currentTarget.blur()}
                className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <span className="text-sm text-gray-500 whitespace-nowrap">
                días vista
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Save button bottom */}
      <div className="flex justify-end">
        <button
          onClick={saveConfig}
          disabled={saving}
          className="flex items-center gap-2 px-5 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors text-sm font-medium"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          {saving ? "Guardando..." : "Guardar cambios"}
        </button>
      </div>
    </div>
  );
}
